import { useEffect, useState } from "react";
import { api } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Timer, TrendingUp, Building2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const rp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const rpShort = (v) => (v >= 1e9 ? `${(v / 1e9).toFixed(1)} M` : v >= 1e6 ? `${(v / 1e6).toFixed(1)} jt` : v >= 1e3 ? `${(v / 1e3).toFixed(0)} rb` : String(v));
const NM = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const labelBulan = (mo) => NM[(Number(String(mo).split("-")[1]) || 1) - 1] || mo;
const MARGIN = { top: 4, right: 8, left: 0, bottom: 0 };
const TICK = { fontSize: 11, fill: "#64748B" };
const RADIUS = [4, 4, 0, 0];

// Ringkasan lembur bulanan gabungan semua area (tampilan sama dgn LemburSummaryCard area + rincian per area)
export default function MasterLemburCard({ area = "all" }) {
  const [data, setData] = useState(null);
  const bulan = new Date().toISOString().slice(0, 7);
  useEffect(() => {
    setData(null);
    api.get("/master/lembur", { params: { bulan, ...(area !== "all" ? { area } : {}) } }).then((r) => setData(r.data)).catch(() => setData({ ringkasan: {}, per_area: [], trend: [] }));
  }, [bulan, area]);
  if (!data) return null;
  const r = data.ringkasan || {};
  const chart = (data.trend || []).map((t) => ({ bulan: labelBulan(t.bulan), biaya: t.total_rupiah, jam: t.jam }));
  const adaTrend = chart.some((c) => c.biaya > 0 || c.jam > 0);
  return (
    <Card className="p-6 border-slate-200 shadow-none rounded-lg" data-testid="master-lembur-card">
      <div className="flex items-center gap-2 mb-4">
        <Timer className="h-5 w-5 text-blue-600" />
        <h3 className="text-base font-bold text-slate-800">Lembur Bulan Ini ({data.bulan || bulan}) — {area === "all" ? "Semua Area" : (data.per_area?.[0]?.area_nama || area)}</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"><p className="text-lg font-extrabold text-emerald-700" data-testid="master-lembur-biaya">{rp(r.total_rupiah)}</p><p className="text-xs">Biaya (disetujui)</p></div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3"><p className="text-lg font-extrabold text-blue-700">{r.jam ?? 0}</p><p className="text-xs">Total Jam</p></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-lg font-extrabold text-slate-700">{r.pekerja ?? 0}</p><p className="text-xs">Pekerja</p></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="text-lg font-extrabold text-slate-700">{r.entri ?? 0}</p><p className="text-xs">Entri</p></div>
      </div>
      {area === "all" && (data.per_area || []).length > 1 && (
        <div className="mt-5 pt-4 border-t border-slate-100" data-testid="master-lembur-per-area">
          <div className="flex items-center gap-2 mb-2"><Building2 className="h-4 w-4 text-amber-600" /><p className="text-sm font-semibold text-slate-700">Per Area</p></div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1.5 pr-3">Area</th><th className="py-1.5 pr-3 text-right">Biaya</th><th className="py-1.5 pr-3 text-right">Jam</th><th className="py-1.5 pr-3 text-right">Pekerja</th><th className="py-1.5 text-right">Entri</th></tr></thead>
              <tbody>{data.per_area.map((p) => (
                <tr key={p.area_kode} className="border-b border-slate-50" data-testid={`master-lembur-area-${p.area_kode}`}>
                  <td className="py-1.5 pr-3 font-semibold text-slate-800">{p.area_nama}</td><td className="py-1.5 pr-3 text-right text-emerald-700 font-bold tabular-nums">{rp(p.total_rupiah)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{p.jam}</td><td className="py-1.5 pr-3 text-right tabular-nums">{p.pekerja}</td><td className="py-1.5 text-right tabular-nums">{p.entri}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
      <div className="mt-5 pt-4 border-t border-slate-100" data-testid="master-lembur-trend">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-emerald-600" /><p className="text-sm font-semibold text-slate-700">Tren Biaya Lembur (6 bulan terakhir)</p></div>
        {adaTrend ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chart} margin={MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="bulan" tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={rpShort} tick={TICK} axisLine={false} tickLine={false} width={48} />
              <Tooltip formatter={(val, name) => (name === "biaya" ? [rp(val), "Biaya disetujui"] : [`${val} jam`, "Total jam"])} />
              <Bar dataKey="biaya" fill="#2563eb" radius={RADIUS} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        ) : <p className="text-sm text-slate-400 py-6 text-center">Belum ada data lembur pada 6 bulan terakhir.</p>}
      </div>
    </Card>
  );
}
